import { Group, Popover, Text, ActionIcon } from "@mantine/core";
import React, { useState } from "react";
import { IconSparkles, IconX } from "@tabler/icons-react";
import { useDiagnosticStore } from "./store";

interface SuggestedCorrectionPopoverProps {
  children: React.ReactNode;
  fieldKey: string;
  copyableField: React.ReactNode;
  onClose: () => void;
}

const SuggestedCorrectionPopover: React.FC<SuggestedCorrectionPopoverProps> = ({
  children,
  copyableField,
  fieldKey,
  onClose,
}) => {
  const { openedPopoverField, setOpenedPopoverField } = useDiagnosticStore();
  const [isHovered, setIsHovered] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  // Show popover if it's opened via store OR if it's being hovered
  const isOpened = openedPopoverField === fieldKey || isHovered;

  const handleClose = (shouldSave: boolean = false) => {
    // Only call onClose if user has actually interacted with the popover
    if (hasInteracted || shouldSave) {
      onClose();
    }

    setOpenedPopoverField(null);
    setIsHovered(false);
    setHasInteracted(false); // Reset for next time
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleDropdownMouseEnter = (e: React.MouseEvent) => {
    // Prevent the popover from closing when hovering over the dropdown content
    e.stopPropagation();
    setIsHovered(true);
  };

  const handleDropdownMouseLeave = (e: React.MouseEvent) => {
    // Prevent the popover from closing when leaving the dropdown content
    e.stopPropagation();
    setIsHovered(false);
  };

  const handleInteraction = () => {
    setHasInteracted(true);
  };

  const handleXClick = () => {
    setHasInteracted(true);
    handleClose(true); // Force save when X is clicked
  };

  return (
    <Popover
      position="bottom"
      withArrow
      shadow="md"
      width={300}
      zIndex={1000}
      opened={isOpened}
      onClose={handleClose}
      closeOnClickOutside
    >
      <Popover.Target>
        <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          {children}
        </div>
      </Popover.Target>
      <Popover.Dropdown
        p="0.75rem"
        onMouseEnter={handleDropdownMouseEnter}
        onMouseLeave={handleDropdownMouseLeave}
        style={{
          // Ensure popover doesn't interfere with cursor behavior
          cursor: "default",
          // Prevent text selection
          userSelect: "none",
          // Ensure pointer events work correctly
          pointerEvents: "auto",
        }}
      >
        <Group gap="0.5rem" justify="space-between">
          <Group gap="0.5rem">
            <Group justify="space-between" w="100%">
              <Group>
                <IconSparkles color="#4E67F6" />
                <Text
                  variant="gradient"
                  gradient={{ from: "#4E69F6", to: "#3CA9FD" }}
                  fw={600}
                  style={{
                    // Ensure text doesn't interfere with cursor
                    cursor: "default",
                    // Prevent text selection
                    userSelect: "none",
                  }}
                >
                  Suggested
                </Text>
              </Group>
              <ActionIcon
                size="sm"
                variant="subtle"
                color="gray"
                onClick={handleXClick}
                style={{
                  // Ensure action icon has consistent cursor
                  cursor: "pointer",
                }}
              >
                <IconX size={14} />
              </ActionIcon>
            </Group>
            <div onClick={handleInteraction}>{copyableField}</div>
          </Group>
        </Group>
      </Popover.Dropdown>
    </Popover>
  );
};

export default SuggestedCorrectionPopover;
